import { role } from '../types/instructions';
/**
 * Service responsible for managing and tracking all agent contexts in the flow.
 * This service provides a centralized way to access agent context variables
 * across the entire application.
 */
export class AgentContextService {
    static $inject = ['BuilderAiAgentService'];
    constructor(BuilderAiAgentService) {
        this.BuilderAiAgentService = BuilderAiAgentService;
        this.agentContexts = new Map();
        this.listeners = [];
    }
    /**
     * Generates an agent context variable name from a stateId
     * @param stateId The state identifier
     * @returns The formatted agent context variable name
     */
    generateAgentContext(stateId) {
        return 'shorttermmemory' + stateId.replace(/[-:]/g, '');
    }
    /**
     * Updates the agent contexts list from the current flow
     * @param flow The current flow view model
     */
    updateFromFlow(flow) {
        if (!flow) {
            this.clear();
            return;
        }
        // Get all agent states from the flow
        const agentStates = this.BuilderAiAgentService.getAgentStates(flow);
        // Clear existing contexts
        this.agentContexts.clear();
        // Build new contexts map
        agentStates.forEach((state) => {
            // Get agent configuration to extract historyLength
            const agentConfig = this.BuilderAiAgentService.getFlowContent(state);
            let historyLength = undefined;
            let hasMemoryEnabled = false;
            if (agentConfig && agentConfig.prompt) {
                const shortTermMemory = agentConfig.prompt.find((item) => {
                    if (typeof item.type !== 'undefined') {
                        return item.type === role.shortTermMemory;
                    }
                    return item.role === 'short-term-memory';
                });
                if (shortTermMemory && 'config' in shortTermMemory) {
                    historyLength = shortTermMemory.config.length;
                    hasMemoryEnabled = true; // Memory is enabled if short-term-memory exists
                }
            }
            const entry = {
                stateId: state.id,
                agentContext: this.generateAgentContext(state.id),
                stateName: state.$title,
                historyLength: historyLength, // Store the agent's history length
                hasMemoryEnabled: hasMemoryEnabled, // Store whether memory is enabled
            };
            this.agentContexts.set(state.id, entry);
        });
        // Notify listeners
        this.notifyListeners();
    }
    /**
     * Adds or updates a single agent context
     * @param stateId The state identifier
     * @param stateName Optional state name/title
     * @param historyLength Optional history length from agent configuration
     */
    addOrUpdateContext(stateId, stateName, historyLength) {
        const hasMemoryEnabled = historyLength !== undefined && historyLength > 0;
        const entry = {
            stateId,
            agentContext: this.generateAgentContext(stateId),
            stateName,
            historyLength,
            hasMemoryEnabled,
        };
        this.agentContexts.set(stateId, entry);
        this.notifyListeners();
    }
    /**
     * Removes an agent context by state ID
     * @param stateId The state identifier to remove
     */
    removeContext(stateId) {
        if (this.agentContexts.delete(stateId)) {
            this.notifyListeners();
        }
    }
    /**
     * Gets the agent context variable name for a specific state ID
     * @param stateId The state identifier
     * @returns The agent context variable name or null if not found
     */
    getContextByStateId(stateId) {
        const entry = this.agentContexts.get(stateId);
        return entry ? entry.agentContext : null;
    }
    /**
     * Gets all agent context entries as an array
     * @returns Array of all agent context entries
     */
    getAllContexts() {
        return Array.from(this.agentContexts.values());
    }
    /**
     * Gets all agent context variable names as an array
     * @returns Array of all agent context variable names
     */
    getAllContextNames() {
        return this.getAllContexts().map(entry => entry.agentContext);
    }
    /**
     * Gets the total count of agent contexts
     * @returns The number of agent contexts
     */
    getCount() {
        return this.agentContexts.size;
    }
    /**
     * Checks if a state ID has an associated agent context
     * @param stateId The state identifier
     * @returns True if the context exists, false otherwise
     */
    hasContext(stateId) {
        return this.agentContexts.has(stateId);
    }
    /**
     * Clears all agent contexts
     */
    clear() {
        this.agentContexts.clear();
        this.notifyListeners();
    }
    /**
     * Subscribes to agent context changes
     * @param callback Function to be called when contexts change
     * @returns Unsubscribe function
     */
    subscribe(callback) {
        this.listeners.push(callback);
        // Immediately call with current state
        callback(this.getAllContexts());
        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(callback);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }
    /**
     * Notifies all listeners of context changes
     */
    notifyListeners() {
        const contexts = this.getAllContexts();
        this.listeners.forEach(listener => listener(contexts));
    }
    /**
     * Gets a formatted list of contexts for display purposes
     * @returns Array of formatted strings with state name and context
     */
    getFormattedContextsList() {
        return this.getAllContexts().map(entry => {
            const name = entry.stateName || entry.stateId;
            return `${name}: {{${entry.agentContext}}}`;
        });
    }
    /**
     * Exports all contexts as a JSON object
     * @returns Object mapping state IDs to context names
     */
    exportAsJson() {
        const result = {};
        this.agentContexts.forEach((entry, stateId) => {
            result[stateId] = entry.agentContext;
        });
        return result;
    }
}
AgentContextService.$inject = ['BuilderAiAgentService'];
